import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from '../supabaseClient'
import EmptyState from '../ui/EmptyState'
import ErrorState from '../ui/ErrorState'
import LoadingState from '../ui/LoadingState'
import SectionHeader from '../ui/SectionHeader'
import StatPill from '../ui/StatPill'
import StatusBadge from '../ui/StatusBadge'
import { PAY_SOURCE_STATUS_OPTIONS, PAY_STATUS_OPTIONS } from './adminConfig'
import { buildSchemaUnavailableMessage, isSchemaMismatchError } from './schemaGuard'
import { formatPlainText, formatTitleText } from '../utils/displayText'

const createBlankForm = () => ({
  worker_id: '',
  job_id: '',
  time_entry_id: '',
  amount_owed: '',
  amount_paid: '',
  payment_status: 'unpaid',
  payment_due_window: '',
  payment_source_status: 'unknown',
  payment_method: '',
  payment_date: '',
  pay_notes: '',
  dispute_flag: false,
  dispute_notes: ''
})

const toFixedCurrency = (value) => {
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return null
  return Number(numeric.toFixed(2))
}

const formatCurrency = (value) => {
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return '$0.00'
  return `$${numeric.toFixed(2)}`
}

const formatDateTime = (value) => {
  if (!value) return 'Unknown'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 'Unknown'
  return date.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

const mapPayRow = (row) => ({
  ...row,
  payment_status: String(row.payment_status || 'unpaid').toLowerCase(),
  payment_source_status: String(row.payment_source_status || 'unknown').toLowerCase(),
  amount_owed: Number(row.amount_owed || 0),
  amount_paid: Number(row.amount_paid || 0),
  dispute_flag: row.dispute_flag === true
})

const toFormFromRow = (row) => ({
  worker_id: row.worker_id ? String(row.worker_id) : '',
  job_id: row.job_id ? String(row.job_id) : '',
  time_entry_id: row.time_entry_id ? String(row.time_entry_id) : '',
  amount_owed: row.amount_owed ?? '',
  amount_paid: row.amount_paid ?? '',
  payment_status: row.payment_status || 'unpaid',
  payment_due_window: row.payment_due_window || '',
  payment_source_status: row.payment_source_status || 'unknown',
  payment_method: row.payment_method || '',
  payment_date: row.payment_date || '',
  pay_notes: row.pay_notes || '',
  dispute_flag: row.dispute_flag === true,
  dispute_notes: row.dispute_notes || ''
})

export default function PayStatusAdminPanel({ onDataChanged }) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [payRecordsUnavailableMessage, setPayRecordsUnavailableMessage] = useState('')

  const [workers, setWorkers] = useState([])
  const [jobs, setJobs] = useState([])
  const [timeEntries, setTimeEntries] = useState([])
  const [records, setRecords] = useState([])
  const [history, setHistory] = useState([])

  const [editingRecordId, setEditingRecordId] = useState('')
  const [form, setForm] = useState(createBlankForm)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    setPayRecordsUnavailableMessage('')

    const [workersResponse, jobsResponse, entriesResponse, recordsResponse, historyResponse] = await Promise.all([
      supabase.from('workers').select('id, name').order('name'),
      supabase.from('jobs').select('id, job_type, job_date').order('job_date', { ascending: false }).limit(200),
      supabase
        .from('employee_time_entries')
        .select('id, worker_id, job_id, entry_date')
        .order('entry_date', { ascending: false })
        .limit(300),
      supabase.from('pay_status_records').select('*').order('updated_at', { ascending: false }).limit(300),
      supabase.from('pay_status_history').select('*').order('created_at', { ascending: false }).limit(300)
    ])

    const entriesSchemaMismatch = isSchemaMismatchError(entriesResponse.error)
    const recordsSchemaMismatch = isSchemaMismatchError(recordsResponse.error)
    const historySchemaMismatch = isSchemaMismatchError(historyResponse.error)

    if (
      workersResponse.error ||
      jobsResponse.error ||
      (entriesResponse.error && !entriesSchemaMismatch) ||
      (recordsResponse.error && !recordsSchemaMismatch)
    ) {
      setError(
        workersResponse.error?.message ||
          jobsResponse.error?.message ||
          entriesResponse.error?.message ||
          recordsResponse.error?.message ||
          'Unable to load pay-status panel.'
      )
      setWorkers([])
      setJobs([])
      setTimeEntries([])
      setRecords([])
      setHistory([])
      setLoading(false)
      return
    }

    setWorkers(workersResponse.data || [])
    setJobs(jobsResponse.data || [])
    setTimeEntries(entriesSchemaMismatch ? [] : entriesResponse.data || [])

    if (recordsSchemaMismatch) {
      setPayRecordsUnavailableMessage(buildSchemaUnavailableMessage('Pay status', 'Manager Time Admin'))
      setRecords([])
      setHistory([])
    } else {
      setRecords((recordsResponse.data || []).map(mapPayRow))
      setHistory(historySchemaMismatch ? [] : historyResponse.data || [])
    }

    setLoading(false)
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const workerById = useMemo(() => new Map(workers.map((worker) => [String(worker.id), worker])), [workers])
  const jobById = useMemo(() => new Map(jobs.map((job) => [String(job.id), job])), [jobs])

  const stats = useMemo(() => {
    const unpaidCount = records.filter((record) => record.payment_status === 'unpaid').length
    const partialCount = records.filter((record) => record.payment_status === 'partial').length
    const disputedCount = records.filter((record) => record.payment_status === 'disputed').length
    const followUpCount = records.filter((record) => ['unpaid', 'partial', 'disputed'].includes(record.payment_status)).length
    return { unpaidCount, partialCount, disputedCount, followUpCount }
  }, [records])

  const handleChange = (field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value
    }))
  }

  const handleEdit = (record) => {
    setEditingRecordId(String(record.id))
    setForm(toFormFromRow(record))
    setMessage('')
  }

  const resetForm = () => {
    setEditingRecordId('')
    setForm(createBlankForm())
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (payRecordsUnavailableMessage) {
      setMessage(payRecordsUnavailableMessage)
      return
    }

    if (!form.worker_id || !form.job_id) {
      setMessage('Worker and job are required.')
      return
    }

    if (String(form.payment_status) === 'disputed' && !formatPlainText(form.dispute_notes)) {
      setMessage('Dispute notes are required when payment status is disputed.')
      return
    }

    const amountOwed = toFixedCurrency(form.amount_owed)
    const amountPaid = toFixedCurrency(form.amount_paid)

    if (amountOwed === null || amountPaid === null) {
      setMessage('Amount owed and amount paid must be numeric values.')
      return
    }

    setSaving(true)
    setMessage('')

    const payload = {
      worker_id: form.worker_id,
      job_id: form.job_id,
      time_entry_id: form.time_entry_id || null,
      amount_owed: amountOwed,
      amount_paid: amountPaid,
      payment_status: String(form.payment_status || 'unpaid').toLowerCase(),
      payment_due_window: formatPlainText(form.payment_due_window),
      payment_source_status: String(form.payment_source_status || 'unknown').toLowerCase(),
      payment_method: formatPlainText(form.payment_method),
      payment_date: form.payment_date || null,
      pay_notes: formatPlainText(form.pay_notes),
      dispute_flag: form.dispute_flag === true || String(form.payment_status || '') === 'disputed',
      dispute_notes: formatPlainText(form.dispute_notes)
    }

    let response
    if (editingRecordId) {
      response = await supabase.from('pay_status_records').update(payload).eq('id', editingRecordId)
    } else {
      response = await supabase.from('pay_status_records').insert([payload])
    }

    if (response.error) {
      setSaving(false)
      setMessage('Unable to save pay-status record: ' + response.error.message)
      return
    }

    setSaving(false)
    setMessage(editingRecordId ? 'Pay-status record updated.' : 'Pay-status record created.')
    resetForm()
    await load()
    onDataChanged?.()
  }

  const handleQuickStatus = async (record, nextStatus) => {
    if (payRecordsUnavailableMessage) {
      setMessage(payRecordsUnavailableMessage)
      return
    }

    setSaving(true)
    setMessage('')

    const response = await supabase
      .from('pay_status_records')
      .update({
        payment_status: nextStatus,
        dispute_flag: nextStatus === 'disputed',
        last_updated_at: new Date().toISOString()
      })
      .eq('id', record.id)

    if (response.error) {
      setSaving(false)
      setMessage('Unable to update status: ' + response.error.message)
      return
    }

    setSaving(false)
    setMessage('Payment status updated.')
    await load()
    onDataChanged?.()
  }

  if (loading) {
    return <LoadingState title="Loading pay statuses" message="Preparing pay visibility records and history." />
  }

  if (error) {
    return <ErrorState title="Pay-status panel unavailable" message={error} />
  }

  return (
    <section className="gg-module-stack">
      <section className="gg-module-panel">
        <SectionHeader
          eyebrow="Admin"
          title="Pay-Status Visibility"
          description="Track unpaid/partial/paid/disputed records with due-window and source visibility."
        />
        <div className="gg-tool-pills">
          <StatPill label="Need Follow-up" value={stats.followUpCount} tone={stats.followUpCount ? 'warning' : 'info'} />
          <StatPill label="Unpaid" value={stats.unpaidCount} tone={stats.unpaidCount ? 'warning' : 'info'} />
          <StatPill label="Partial" value={stats.partialCount} tone={stats.partialCount ? 'warning' : 'info'} />
          <StatPill label="Disputed" value={stats.disputedCount} tone={stats.disputedCount ? 'error' : 'info'} />
        </div>
        {payRecordsUnavailableMessage ? <p className="gg-inline-alert gg-inline-alert--warning">{payRecordsUnavailableMessage}</p> : null}
      </section>

      <section className="gg-module-panel">
        <p className="gg-module-panel__label">{editingRecordId ? 'Edit Pay-Status Record' : 'Create Pay-Status Record'}</p>
        {payRecordsUnavailableMessage ? (
          <EmptyState
            title="Pay status records unavailable"
            message="Pay status tables are not enabled in this environment. Use Time Admin for shift review and correction testing."
          />
        ) : (
          <form className="gg-form-grid" onSubmit={handleSubmit}>
            <label className="gg-form-field">
              <span>Worker *</span>
              <select className="gg-select" value={form.worker_id} onChange={(event) => handleChange('worker_id', event.target.value)}>
                <option value="">Select worker</option>
                {workers.map((worker) => (
                  <option key={worker.id} value={worker.id}>
                    {formatTitleText(worker.name, 'Worker')}
                  </option>
                ))}
              </select>
            </label>
            <label className="gg-form-field">
              <span>Job *</span>
              <select className="gg-select" value={form.job_id} onChange={(event) => handleChange('job_id', event.target.value)}>
                <option value="">Select job</option>
                {jobs.map((job) => (
                  <option key={job.id} value={job.id}>
                    {formatTitleText(job.job_type, 'Job')} • {job.job_date || 'No date'}
                  </option>
                ))}
              </select>
            </label>
            <label className="gg-form-field">
              <span>Time Entry</span>
              <select className="gg-select" value={form.time_entry_id} onChange={(event) => handleChange('time_entry_id', event.target.value)}>
                <option value="">Not linked</option>
                {timeEntries.map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    {entry.id} • {entry.entry_date || 'No date'}
                  </option>
                ))}
              </select>
            </label>
            <label className="gg-form-field">
              <span>Amount Owed *</span>
              <input className="gg-input" type="number" step="0.01" value={form.amount_owed} onChange={(event) => handleChange('amount_owed', event.target.value)} />
            </label>
            <label className="gg-form-field">
              <span>Amount Paid *</span>
              <input className="gg-input" type="number" step="0.01" value={form.amount_paid} onChange={(event) => handleChange('amount_paid', event.target.value)} />
            </label>
            <label className="gg-form-field">
              <span>Payment Status</span>
              <select className="gg-select" value={form.payment_status} onChange={(event) => handleChange('payment_status', event.target.value)}>
                {PAY_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="gg-form-field">
              <span>Payment Due Window</span>
              <input className="gg-input" value={form.payment_due_window} onChange={(event) => handleChange('payment_due_window', event.target.value)} placeholder="Net 7 / End of week" />
            </label>
            <label className="gg-form-field">
              <span>Payment Source Status</span>
              <select className="gg-select" value={form.payment_source_status} onChange={(event) => handleChange('payment_source_status', event.target.value)}>
                {PAY_SOURCE_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="gg-form-field">
              <span>Payment Method</span>
              <input className="gg-input" value={form.payment_method} onChange={(event) => handleChange('payment_method', event.target.value)} />
            </label>
            <label className="gg-form-field">
              <span>Payment Date</span>
              <input className="gg-input" type="date" value={form.payment_date} onChange={(event) => handleChange('payment_date', event.target.value)} />
            </label>
            <label className="gg-form-field">
              <span>Dispute Flag</span>
              <input type="checkbox" checked={form.dispute_flag === true} onChange={(event) => handleChange('dispute_flag', event.target.checked)} />
            </label>
            <label className="gg-form-field">
              <span>Dispute Notes</span>
              <textarea className="gg-textarea" value={form.dispute_notes} onChange={(event) => handleChange('dispute_notes', event.target.value)} />
            </label>
            <label className="gg-form-field">
              <span>Pay Notes</span>
              <textarea className="gg-textarea" value={form.pay_notes} onChange={(event) => handleChange('pay_notes', event.target.value)} />
            </label>
            <div className="gg-form-actions">
              <button type="submit" className="gg-button gg-button--primary" disabled={saving}>
                {saving ? 'Saving...' : editingRecordId ? 'Save Pay Status' : 'Create Pay Status'}
              </button>
              {editingRecordId ? (
                <button type="button" className="gg-button gg-button--ghost" onClick={resetForm}>
                  Cancel Edit
                </button>
              ) : null}
            </div>
          </form>
        )}
        {message ? (
          <p
            className={`gg-inline-alert ${
              message.toLowerCase().includes('unable')
                ? 'gg-inline-alert--error'
                : message.toLowerCase().includes('unavailable')
                  ? 'gg-inline-alert--warning'
                  : 'gg-inline-alert--success'
            }`}
          >
            {message}
          </p>
        ) : null}
      </section>

      <section className="gg-module-panel">
        <p className="gg-module-panel__label">Pay-Status Queue</p>
        {payRecordsUnavailableMessage ? (
          <EmptyState title="Pay-status queue unavailable" message="Pay-status records table is not present in this environment." />
        ) : records.length === 0 ? (
          <EmptyState title="No pay-status records" message="Create a record to track payment visibility." />
        ) : (
          <div className="gg-record-list">
            {records.map((record) => {
              const worker = workerById.get(String(record.worker_id))
              const job = jobById.get(String(record.job_id))
              const shortHistory = history.filter((row) => String(row.pay_status_id) === String(record.id)).slice(0, 2)

              return (
                <article key={record.id} className="gg-record-item">
                  <div className="gg-record-item__head">
                    <div>
                      <p className="gg-record-item__title">{formatTitleText(worker?.name, 'Worker')} • {formatTitleText(job?.job_type, 'Job')}</p>
                      <p className="gg-record-item__meta">Due {record.payment_due_window || 'Not set'} • Source {formatTitleText(record.payment_source_status, 'Unknown')}</p>
                      <p className="gg-record-item__meta">Owed {formatCurrency(record.amount_owed)} • Paid {formatCurrency(record.amount_paid)}</p>
                      <p className="gg-record-item__meta">Updated {formatDateTime(record.last_updated_at || record.updated_at || record.created_at)}</p>
                    </div>
                    <StatusBadge
                      status={
                        record.payment_status === 'paid'
                          ? 'success'
                          : record.payment_status === 'disputed'
                            ? 'error'
                            : 'warning'
                      }
                      label={formatTitleText(record.payment_status, 'Unpaid')}
                    />
                  </div>
                  {record.dispute_notes ? <p className="gg-record-item__meta">Dispute: {record.dispute_notes}</p> : null}
                  {record.pay_notes ? <p className="gg-record-item__meta">Notes: {record.pay_notes}</p> : null}
                  {shortHistory.length > 0 ? (
                    <p className="gg-record-item__meta">
                      History: {shortHistory.map((item) => `${formatTitleText(item.next_payment_status, 'unknown')} @ ${formatDateTime(item.created_at)}`).join(' • ')}
                    </p>
                  ) : null}
                  <div className="gg-form-actions">
                    <button type="button" className="gg-button gg-button--ghost" onClick={() => handleEdit(record)} disabled={saving}>
                      Edit
                    </button>
                    <button type="button" className="gg-button gg-button--ghost" onClick={() => handleQuickStatus(record, 'unpaid')} disabled={saving}>
                      Mark Unpaid
                    </button>
                    <button type="button" className="gg-button gg-button--ghost" onClick={() => handleQuickStatus(record, 'partial')} disabled={saving}>
                      Mark Partial
                    </button>
                    <button type="button" className="gg-button gg-button--ghost" onClick={() => handleQuickStatus(record, 'paid')} disabled={saving}>
                      Mark Paid
                    </button>
                    <button type="button" className="gg-button gg-button--ghost" onClick={() => handleQuickStatus(record, 'disputed')} disabled={saving}>
                      Mark Disputed
                    </button>
                  </div>
                </article>
              )
            })}
          </div>
        )}
      </section>
    </section>
  )
}
